/**
 * Survey-list reads and the one survey-list write: creating a survey in the
 * field.
 *
 * The list comes from IndexedDB, never from the network. A surveyor who opens
 * the app in a basement sees every survey on the device, including the ones
 * that have never reached the office — which is the whole point of the offline
 * store being the system of record while the survey is being taken.
 */

import { useCallback, useEffect, useState } from 'react';

import { isoAt, type OutboxOwed, type RemovalPath, type StoredSurvey } from '../../db/store';
import { emptySurveyDocument, newSurveyId } from './newSurvey';
import { useServices } from './services';
import { forgetWalked } from './walked';

export interface SurveyListState {
  records: StoredSurvey[];
  /**
   * What each survey still owes the office, by id (D-052 §2). Read in the
   * same refresh as the records: the outbox is in the same database and every
   * ack emits the same store event, so a *Submitted* row's "still sending"
   * line counts down as the queue drains.
   */
  owed: ReadonlyMap<string, OutboxOwed>;
  loading: boolean;
  error: string | null;
}

const NOTHING_OWED: ReadonlyMap<string, OutboxOwed> = new Map();

/** Every survey on this device, refreshed on every committed store write. */
export function useSurveyList(): SurveyListState {
  const { store } = useServices();
  const [state, setState] = useState<SurveyListState>({
    records: [],
    owed: NOTHING_OWED,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    const refresh = (): void => {
      void Promise.all([store.listSurveys(), store.outboxOwedBySurvey()])
        .then(([records, owed]) => {
          if (!cancelled) setState({ records, owed, loading: false, error: null });
        })
        .catch((cause: unknown) => {
          if (!cancelled) {
            setState({
              records: [],
              owed: NOTHING_OWED,
              loading: false,
              error: cause instanceof Error ? cause.message : 'Could not read this device.',
            });
          }
        });
    };

    refresh();
    const unsubscribe = store.subscribe(refresh);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [store]);

  return state;
}

export interface CreateFieldSurveyInput {
  /** Optional head start from the "New Survey" sheet. Both may be empty. */
  buildingName?: string;
  address?: string;
}

/**
 * Create a survey on site (spec §2.1) and return its id.
 *
 * The order here is the contract:
 *
 *  1. The document is written to IndexedDB FIRST, inside the transaction that
 *     also queues BOTH its push and its creation notice. From this instant the
 *     survey exists — and so does the promise to tell the office about it —
 *     whatever happens to the tab, the battery, or the signal.
 *  2. A drain is kicked. Online, it runs immediately: `POST /api/surveys` fires
 *     the Telegram notice to Chip and Tim — "no job may exist they don't know
 *     about". Offline, `drain()` reports `skipped: 'offline'` and the queued row
 *     simply waits for the first reconnect, when the same row fires the same
 *     notice.
 *
 * The notice is its own outbox row (`kind: 'create'`), not a side effect of the
 * document push. It used to be the latter, and that made it exactly as durable
 * as a row `ackSubmission` deletes: a survey created and submitted in one
 * offline session announced itself to nobody. See `SyncEngine.sendCreation` in
 * src/db/sync.ts and `ApiClient.createSurvey` in src/db/http-transport.ts.
 */
export function useCreateFieldSurvey(): (input?: CreateFieldSurveyInput) => Promise<string> {
  const { store, sync, identity } = useServices();

  return useCallback(
    async (input: CreateFieldSurveyInput = {}) => {
      const surveyId = newSurveyId();
      const createdAt = isoAt(store.now());

      const document = emptySurveyDocument({
        surveyId,
        origin: 'field_initiated',
        createdAt,
        ...(identity ? { createdBy: identity.id, assignedTo: identity.id } : {}),
      });

      // The building name and address the surveyor typed on the sheet are B1
      // and B2 — ordinary answers, not a second place to keep the same fact.
      if (input.buildingName?.trim()) {
        document.answers['B1'] = { kind: 'text', value: input.buildingName.trim() };
      }
      if (input.address?.trim()) {
        document.answers['B2'] = { kind: 'text', value: input.address.trim() };
      }

      await store.createSurvey(document);

      // Fire-and-forget: a failed drain leaves the row queued and the pill
      // showing it. Awaiting it would make "+ New Survey" hang on a bad link.
      void sync.drain().catch(() => undefined);

      return surveyId;
    },
    [store, sync, identity],
  );
}

/**
 * Remove a survey from this device (D-012).
 *
 * Two acts behind one call, and the caller says which — `removalPathFor` in
 * store.ts decides it from real sync state, and the screen has already shown the
 * surveyor a confirmation whose words match that decision. Passing it in rather
 * than re-deciding here is what keeps the confirmation and the act from ever
 * disagreeing: the store re-checks and throws if they do.
 *
 *   'local_delete' — the document, the photo bytes and every outbox row go, in
 *                    one transaction. Nothing is queued: the server never knew.
 *   'discard'      — the same locally, plus ONE queued row that tells the
 *                    office. Offline that row simply waits, exactly like every
 *                    other mutation, and survives a reload; the drain kicked
 *                    below is an optimisation, not the mechanism.
 *
 * The drain is fire-and-forget for the same reason creation's is: the promise
 * is already durable, and making the button hang on a bad link would only teach
 * the surveyor to tap it twice.
 */
export function useRemoveSurvey(): (surveyId: string, path: RemovalPath) => Promise<void> {
  const { store, sync } = useServices();

  return useCallback(
    async (surveyId: string, path: RemovalPath) => {
      if (path === 'sealed_admin_only') {
        // Unreachable from the UI — the screen renders an explanation instead of
        // a control — and loud rather than silent if a future caller forgets.
        throw new Error('useRemoveSurvey: a submitted survey can only be removed by the office');
      }

      if (path === 'local_delete') {
        await store.deleteSurveyLocal(surveyId);
        // The device's walk trail is not survey data (walked.ts) so the store
        // knows nothing about it — but "get this survey off my phone" has to
        // mean all of it, and a stale trail keyed to a dead id would sit in
        // localStorage forever.
        forgetWalked(surveyId);
        return;
      }

      await store.discardSurvey(surveyId);
      forgetWalked(surveyId);
      void sync.drain().catch(() => undefined);
    },
    [store, sync],
  );
}
